import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileDown, Loader2, Pencil } from 'lucide-react';
import {
  qrTemplates,
  QrTemplate,
  QrTemplateBusinessInfo,
  QrMode,
} from '@/pages/qrTemplates';
import {
  canvasToPngDataUrl,
  downloadCanvasAsPng,
  loadQrImageFromCanvas,
  renderTemplateToCanvas,
  sanitizeFileName,
} from '@/pages/qrTemplates/render';
import QrTemplateEditorDialog from './QrTemplateEditorDialog';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  qrCanvas: HTMLCanvasElement | null;
  business: QrTemplateBusinessInfo;
  mode: QrMode;
}

interface PreviewState {
  loading: boolean;
  error: string | null;
  dataUrls: Record<string, string>;
}

const QrTemplatePickerDialog: React.FC<Props> = ({ open, onOpenChange, qrCanvas, business, mode }) => {
  const [preview, setPreview] = useState<PreviewState>({ loading: false, error: null, dataUrls: {} });
  const [editorTemplate, setEditorTemplate] = useState<QrTemplate | null>(null);

  useEffect(() => {
    if (!open) return;
    if (!qrCanvas) {
      setPreview({ loading: false, error: 'QR-Code nicht verfügbar.', dataUrls: {} });
      return;
    }
    let cancelled = false;
    setPreview({ loading: true, error: null, dataUrls: {} });
    (async () => {
      try {
        const qrImage = await loadQrImageFromCanvas(qrCanvas);
        const dataUrls: Record<string, string> = {};
        for (const template of qrTemplates) {
          if (cancelled) return;
          try {
            const canvas = renderTemplateToCanvas(template, qrImage, business, mode);
            dataUrls[template.id] = canvasToPngDataUrl(canvas);
          } catch (err) {
            console.error(`Template render failed: ${template.id}`, err);
          }
          await new Promise((r) => setTimeout(r, 0));
        }
        if (!cancelled) {
          setPreview({ loading: false, error: null, dataUrls });
        }
      } catch (err) {
        console.error('Template preview generation failed:', err);
        if (!cancelled) {
          setPreview({ loading: false, error: 'Vorschau konnte nicht erzeugt werden.', dataUrls: {} });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, qrCanvas, business, mode]);

  const handleDownloadPlain = () => {
    if (!qrCanvas) return;
    const safe = sanitizeFileName(business.name);
    downloadCanvasAsPng(qrCanvas, `${safe}-${mode}.png`);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Template wählen</DialogTitle>
            <DialogDescription>
              Wählen Sie ein Design aus — im nächsten Schritt können Sie Texte, Positionen und Farben anpassen.
            </DialogDescription>
          </DialogHeader>

          {preview.error && (
            <div className="text-sm text-destructive-strong py-2">{preview.error}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 py-2">
            {qrTemplates.map((template) => {
              const url = preview.dataUrls[template.id];
              return (
                <button
                  key={template.id}
                  type="button"
                  disabled={!url}
                  onClick={() => setEditorTemplate(template)}
                  className="group flex flex-col rounded-lg border border-border overflow-hidden bg-card hover:border-[#1967D2] hover:shadow-md transition-all text-left disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <div className="aspect-[4/5] bg-muted flex items-center justify-center overflow-hidden">
                    {preview.loading && !url ? (
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    ) : url ? (
                      <img
                        src={url}
                        alt={template.name}
                        className="max-w-full max-h-full object-contain p-2"
                      />
                    ) : (
                      <div className="text-xs text-muted-foreground text-center px-4">Vorschau nicht verfügbar</div>
                    )}
                  </div>
                  <div className="p-3 border-t border-border">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-foreground text-sm">{template.name}</div>
                      <Pencil className="h-4 w-4 text-muted-foreground group-hover:text-[#1967D2] shrink-0 transition-colors" />
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{template.format}</div>
                    <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{template.description}</div>
                    <div className="text-[11px] text-[#1967D2] mt-2 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Anpassen &amp; herunterladen →
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 border-t pt-4">
            <Button variant="ghost" onClick={handleDownloadPlain} disabled={!qrCanvas}>
              <FileDown className="h-4 w-4 mr-2" />
              Nur QR-Code (ohne Template)
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QrTemplateEditorDialog
        open={editorTemplate !== null}
        onClose={() => setEditorTemplate(null)}
        template={editorTemplate}
        qrCanvas={qrCanvas}
        business={business}
        mode={mode}
      />
    </>
  );
};

export default QrTemplatePickerDialog;
